import { PERMISSIONS, type PermissionCode } from '@/lib/permissions'

/**
 * The loans area, as the router sees it.
 *
 * Everything the integrator needs is here: the components by name, and {@link loansRoutes}
 * describing the paths they sit at, the permission each screen is for, and what it does on a
 * phone. Nothing below reaches into the router — the routes are declared in `src/router.tsx` and
 * this is the description they are declared from.
 *
 * `LoansPage` is exported as an alias of {@link LoanAccountListPage} so that the existing
 * `/loans` route keeps working unchanged while the rest of the tree is wired.
 */

export { LoanAccountListPage, LoanAccountListPage as LoansPage } from './loan-account-list'
export { LoanAccountNewPage } from './loan-account-new'
export { LoanAccountDetailPage } from './loan-account-detail'
export { ApplyForRoundPage } from './apply-for-round'
export { LoanCycleDetailPage } from './loan-cycle-detail'
export {
  LoanAppraisalPage,
  LoanApprovalPage,
  LoanClassificationOverridePage,
  LoanDisbursementPage,
  LoanGuaranteeOffsetPage,
  LoanPenaltyWaiverPage,
  LoanRecoveryPage,
  LoanRepaymentPage,
  LoanRestructurePage,
  LoanSettlementPage,
  LoanWriteOffPage,
} from './cycle-operations'
export { LoanProductListPage } from './loan-product-list'
export {
  LoanProductDetailPage,
  LoanProductEditPage,
  LoanProductNewPage,
} from './loan-product-pages'
export { LoanPortfolioPage } from './loan-portfolio'
export { ClassificationSchemeNewPage } from './classification-scheme-new'
export { LoanDisclosureConfigPage } from './disclosure-config'

export {
  DEFAULT_PAGE_SIZE as LOAN_DEFAULT_PAGE_SIZE,
  loanAccountDetailSearchSchema,
  loanAccountListSearchSchema,
  loanCycleDetailSearchSchema,
  loanProductListSearchSchema,
} from './search'

export interface LoanRouteDescriptor {
  /** The path, exactly as `loanPaths` in `features/loans/loan-paths.ts` builds it. */
  path: string
  /** The named export above that renders it. */
  componentName: string
  /**
   * The permission the screen is *for*. It is not a guard: the screens gate their own controls
   * and the server authorises every request, so a route registered without a `beforeLoad` check
   * is safe — it simply shows a permission notice instead of a form.
   */
  permission: PermissionCode
  /** What the screen does below the `lg` breakpoint, per docs/mobile-policy.md. */
  phoneBehaviour: 'adapts' | 'refuses'
  /** Which search schema, where one applies. */
  searchSchema?: string
}

/**
 * Every screen in this area.
 *
 * The eleven operation routes are separate paths rather than one parameterised `$operation`
 * because each carries its own permission, and a route list where the permission column reads
 * "it depends" is one nobody can review.
 */
export const loansRoutes: LoanRouteDescriptor[] = [
  {
    path: '/loans',
    componentName: 'LoanAccountListPage',
    permission: PERMISSIONS.loans.view,
    phoneBehaviour: 'adapts',
    searchSchema: 'loanAccountListSearchSchema',
  },
  {
    path: '/loans/new',
    componentName: 'LoanAccountNewPage',
    permission: PERMISSIONS.loans.accountOpen,
    phoneBehaviour: 'adapts',
  },
  {
    path: '/loans/products',
    componentName: 'LoanProductListPage',
    permission: PERMISSIONS.loans.productView,
    phoneBehaviour: 'adapts',
    searchSchema: 'loanProductListSearchSchema',
  },
  {
    path: '/loans/products/new',
    componentName: 'LoanProductNewPage',
    permission: PERMISSIONS.loans.productManage,
    phoneBehaviour: 'refuses',
  },
  {
    path: '/loans/products/$productId',
    componentName: 'LoanProductDetailPage',
    permission: PERMISSIONS.loans.productView,
    phoneBehaviour: 'adapts',
  },
  {
    path: '/loans/products/$productId/edit',
    componentName: 'LoanProductEditPage',
    permission: PERMISSIONS.loans.productManage,
    phoneBehaviour: 'refuses',
  },
  {
    path: '/loans/portfolio',
    componentName: 'LoanPortfolioPage',
    permission: PERMISSIONS.loans.view,
    phoneBehaviour: 'refuses',
  },
  {
    path: '/loans/classification-schemes/new',
    componentName: 'ClassificationSchemeNewPage',
    permission: PERMISSIONS.loans.classificationConfig,
    phoneBehaviour: 'refuses',
  },
  {
    path: '/loans/disclosure-config',
    componentName: 'LoanDisclosureConfigPage',
    permission: PERMISSIONS.loans.disclosureConfig,
    phoneBehaviour: 'refuses',
  },
  {
    path: '/loans/$accountId',
    componentName: 'LoanAccountDetailPage',
    permission: PERMISSIONS.loans.view,
    phoneBehaviour: 'adapts',
    searchSchema: 'loanAccountDetailSearchSchema',
  },
  {
    path: '/loans/$accountId/apply',
    componentName: 'ApplyForRoundPage',
    permission: PERMISSIONS.loans.apply,
    phoneBehaviour: 'refuses',
  },
  {
    path: '/loans/$accountId/cycles/$cycleId',
    componentName: 'LoanCycleDetailPage',
    permission: PERMISSIONS.loans.view,
    phoneBehaviour: 'adapts',
    searchSchema: 'loanCycleDetailSearchSchema',
  },
  {
    path: '/loans/$accountId/cycles/$cycleId/appraise',
    componentName: 'LoanAppraisalPage',
    permission: PERMISSIONS.loans.appraise,
    phoneBehaviour: 'refuses',
  },
  {
    path: '/loans/$accountId/cycles/$cycleId/approve',
    componentName: 'LoanApprovalPage',
    permission: PERMISSIONS.loans.approve,
    phoneBehaviour: 'refuses',
  },
  {
    path: '/loans/$accountId/cycles/$cycleId/disburse',
    componentName: 'LoanDisbursementPage',
    permission: PERMISSIONS.loans.disburse,
    phoneBehaviour: 'refuses',
  },
  {
    path: '/loans/$accountId/cycles/$cycleId/repay',
    componentName: 'LoanRepaymentPage',
    permission: PERMISSIONS.loans.repaymentPost,
    phoneBehaviour: 'refuses',
  },
  {
    path: '/loans/$accountId/cycles/$cycleId/settle',
    componentName: 'LoanSettlementPage',
    permission: PERMISSIONS.loans.repaymentPost,
    phoneBehaviour: 'refuses',
  },
  {
    path: '/loans/$accountId/cycles/$cycleId/waive',
    componentName: 'LoanPenaltyWaiverPage',
    permission: PERMISSIONS.loans.penaltyWaive,
    phoneBehaviour: 'refuses',
  },
  {
    path: '/loans/$accountId/cycles/$cycleId/restructure',
    componentName: 'LoanRestructurePage',
    permission: PERMISSIONS.loans.restructure,
    phoneBehaviour: 'refuses',
  },
  {
    path: '/loans/$accountId/cycles/$cycleId/write-off',
    componentName: 'LoanWriteOffPage',
    permission: PERMISSIONS.loans.writeOff,
    phoneBehaviour: 'refuses',
  },
  {
    path: '/loans/$accountId/cycles/$cycleId/offset',
    componentName: 'LoanGuaranteeOffsetPage',
    permission: PERMISSIONS.loans.guaranteeOffset,
    phoneBehaviour: 'refuses',
  },
  {
    path: '/loans/$accountId/cycles/$cycleId/recovery',
    componentName: 'LoanRecoveryPage',
    permission: PERMISSIONS.loans.repaymentPost,
    phoneBehaviour: 'refuses',
  },
  {
    path: '/loans/$accountId/cycles/$cycleId/classify',
    componentName: 'LoanClassificationOverridePage',
    permission: PERMISSIONS.loans.classificationOverride,
    phoneBehaviour: 'refuses',
  },
]
